import { useState } from 'react';
import { adjustVoucher, getVoucherMovements, issueVoucher, redeemVoucher, searchCustomersByPhone } from '../services/voucherService';

function getVoucherActionError(error, fallbackMessage) {
  const status = error?.response?.status;
  const apiMessage = error?.response?.data?.message || error?.response?.data;

  if (status === 409) {
    return typeof apiMessage === 'string' && apiMessage.trim()
      ? apiMessage
      : 'No hay horas disponibles suficientes para realizar ese canje.';
  }

  return typeof apiMessage === 'string' && apiMessage.trim()
    ? apiMessage
    : fallbackMessage;
}

export function useVoucherAdmin(actor) {
  const [results, setResults] = useState([]);
  const [resultsMeta, setResultsMeta] = useState({
    page: 0,
    size: 10,
    totalElements: 0,
    totalPages: 1,
    sortBy: 'surname',
    direction: 'asc',
    voucherPage: 0,
    voucherSize: 5,
    voucherSortBy: 'expiresAt',
    voucherDirection: 'asc',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [busyVoucherId, setBusyVoucherId] = useState(null);
  const [lastPhone, setLastPhone] = useState('');

  async function searchByPhone(phone, params = {}) {
    setLoading(true);
    setError('');
    setLastPhone(phone);

    const nextParams = {
      page: params.page ?? resultsMeta.page,
      size: params.size ?? resultsMeta.size,
      sortBy: params.sortBy ?? resultsMeta.sortBy,
      direction: params.direction ?? resultsMeta.direction,
      voucherPage: params.voucherPage ?? resultsMeta.voucherPage,
      voucherSize: params.voucherSize ?? resultsMeta.voucherSize,
      voucherSortBy: params.voucherSortBy ?? resultsMeta.voucherSortBy,
      voucherDirection: params.voucherDirection ?? resultsMeta.voucherDirection,
    };

    try {
      const response = await searchCustomersByPhone(phone, nextParams);
      setResults(response.items);
      setResultsMeta({
        ...nextParams,
        page: response.page,
        size: response.size,
        totalElements: response.totalElements,
        totalPages: response.totalPages,
      });
    } catch (_error) {
      setResults([]);
      setResultsMeta((previous) => ({ ...previous, totalElements: 0, totalPages: 1 }));
      setError('No se pudo buscar por número de móvil.');
    } finally {
      setLoading(false);
    }
  }

  async function createVoucherForCustomer(payload) {
    setBusyVoucherId(payload.customerId ? `issue-${payload.customerId}` : 'issue-new');

    try {
      await issueVoucher(payload);
      if (payload.phone) {
        await searchByPhone(payload.phone, { page: 0 });
      }
    } finally {
      setBusyVoucherId(null);
    }
  }

  async function redeemVoucherHours(voucherId, payload, phone) {
    setBusyVoucherId(voucherId);
    setError('');

    const customerVoucher = results
      .flatMap((customer) => customer.vouchers || [])
      .find((voucher) => voucher.id === voucherId);

    if (payload.hours > 0 && customerVoucher && payload.hours > customerVoucher.hoursAvailable) {
      setBusyVoucherId(null);
      setError('No hay horas disponibles suficientes para realizar ese canje.');
      return false;
    }

    try {
      await redeemVoucher(voucherId, {
        ...payload,
        performedByUserId: actor?.id || null,
        performedByName: actor?.name || actor?.fullName || actor?.email || 'Operador',
      });
      await searchByPhone(phone, { page: resultsMeta.page });
      return true;
    } catch (error) {
      setError(getVoucherActionError(error, 'No se pudo realizar el canje.'));
      return false;
    } finally {
      setBusyVoucherId(null);
    }
  }

  async function adjustVoucherHours(voucherId, payload, phone) {
    setBusyVoucherId(voucherId);

    const customerVoucher = results
      .flatMap((customer) => customer.vouchers || [])
      .find((voucher) => voucher.id === voucherId);

    if (payload.hours < 0 && customerVoucher && customerVoucher.hoursAvailable + payload.hours < 0) {
      setBusyVoucherId(null);
      throw new Error('No hay horas disponibles suficientes para aplicar ese ajuste.');
    }

    try {
      await adjustVoucher(voucherId, {
        ...payload,
        performedByUserId: actor?.id || null,
        performedByName: actor?.name || actor?.fullName || actor?.email || 'Operador',
      });
      await searchByPhone(phone, { page: resultsMeta.page });
    } catch (_error) {
      throw new Error('No se pudo guardar el ajuste.');
    } finally {
      setBusyVoucherId(null);
    }
  }

  async function loadVoucherMovements(voucherId, params = {}) {
    return getVoucherMovements(voucherId, params);
  }

  return {
    results,
    resultsMeta,
    loading,
    error,
    busyVoucherId,
    lastPhone,
    searchByPhone,
    createVoucherForCustomer,
    redeemVoucherHours,
    adjustVoucherHours,
    loadVoucherMovements,
  };
}
